<template>
  <div :class="$style.modal">
    <ModalHeader
      :handleClose="handleClose"
      :headerSubtext="headerSubtext"
      :headerText="getHeaderText"
      :showClose="true"
    />
    <TableWithEndlessScroll
      :class="$style.table"
      :columns="columns"
      :isRefreshing="isRefreshing"
      :loadingAdditionalData="loadingAdditionalData"
      :moreToLoad="moreToLoad"
      :tableData="tableData"
      @cellClick="handleCellClick"
      @loadMore="fetchBoundApplications"
    >
      <template v-slot:emptyMessage>
        <p>
          User is not associated with any applications.
        </p>
      </template>
    </TableWithEndlessScroll>
    <ModalFooter
      :handleClose="handleClose"
      :handleMainAction="handleClose"
      mainActionText="close"
    />
  </div>
</template>
<script>
import * as Constants from 'client/util/Constants';
import {
  getAppItemTableColumnSettings,
} from 'client/applications/ApplicationItemTable/ApplicationItemTableColumns';
import { notify } from 'client/util';

import ApplicationApi from 'client/api/v2/ApplicationApi';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalFooter from 'components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import TableWithEndlessScroll from 'components/Tables/TableWithEndlessScroll.vue';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Util from 'client/util/Util';

const { ItemTypeConsts, ItemTypeRoutes } = Constants;
const headerSubtext = 'Applications are associated to users by the Groups they are assigned to.';

// If it is found that users are seeing performance issues,
// could drop this to as low as 10 per fetch
const maxApplicationsPerFetch = 50;

const errorMessage = 'Error retrieving applications for user.';

const ApplicationItemTableColumns = getAppItemTableColumnSettings();

// Sort is removed from each column, since the bound applications endpoint does not support it
const columns = [
  {
    ...ApplicationItemTableColumns.status,
    sortBy: undefined,
  }, {
    ...ApplicationItemTableColumns.name,
    sortBy: undefined,
  }, {
    bodyComponent: 'ItemName',
    bodyComponentProps: {
      itemType: ItemTypeConsts.sso,
      showAsLink: true,
      titleField: 'displayLabel',
    },
    name: 'Display Label',
  }, {
    bodyComponent: 'UserGroupListWithLink',
    name: 'Groups',
  },
];

// There is a lot of logic, both here and in the styling of this component
// A future ticket will be implemented on the BE to fetch this data in one call
// Once that is done, this component will be able to leverage the ItemTable
// Allowing us to remove much of the convuluted logic
export default {
  name: 'UserHighlightsApplicationsModal',

  components: {
    ModalFooter,
    ModalHeader,
    TableWithEndlessScroll,
  },

  props: {
    totalCount: {
      required: true,
      type: Number,
    },
    userId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      columns,
      headerSubtext,
      isRefreshing: true,
      loadingAdditionalData: false,
      skip: 0,
      tableData: [],
      boundUserGroupCache: [],
    };
  },

  computed: {
    getHeaderText() {
      return `Applications (${this.totalCount})`;
    },

    moreToLoad() {
      return this.totalCount > this.tableData.length;
    },
  },

  created() {
    if (this.moreToLoad) {
      this.handleLoadMore().finally(() => { this.isRefreshing = false; });
    }
  },

  methods: {
    determineIndividualUserGroupName({ id }) {
      const cachedGroup = this.boundUserGroupCache
        .find(({ id: cachedGroupId }) => cachedGroupId === id);

      return cachedGroup;
    },

    async fetchAllUserGroupsBoundToUser() {
      // could present inaccurate data when a user is associated with > 100 user groups
      // only fetch once, do not fetch when reloading
      if (this.boundUserGroupCache.length) return;

      // returns all id values for usergroups bound to user
      const { results } = await SystemUserApi.getBoundUserGroups(this.userId, {
        limit: 100,
      });

      const groupIds = results.map(d => d.id);

      // returns data for each specific userGroup
      const data = await Promise.all(groupIds.map(id => UserGroupApi.get(id)));
      this.boundUserGroupCache = data.map(d => ({
        id: d.id,
        name: d.name,
      }));
    },

    async fetchBoundApplications() {
      return SystemUserApi.getBoundApplications(this.userId, {
        limit: maxApplicationsPerFetch,
        skip: this.tableData.length,
      });
    },

    async fetchIndividualApplicationData({ id }) {
      // for each individual applcation, get name and bound user group
      const [application, userGroups] = await Promise
        .all([
          ApplicationApi.get(id),
          ApplicationApi.fetchUserGroups(id),
        ]);

      const userGroupList = userGroups
        .map(this.determineIndividualUserGroupName)
        // filter userGroups bound to application, but not to user
        .filter(userGroup => !!userGroup);

      return {
        ...application,
        userGroupList,
      };
    },

    handleCellClick({ dataField }, value) {
      const { id } = this.tableData.find(item => item[dataField] === value);
      const href = `${ItemTypeRoutes[ItemTypeConsts.application]}/${id}/details`;
      Util.redirect(href);
      this.handleClose();
    },

    handleClose() {
      // reset boundUserGroupCache so it is empty for next user
      this.boundUserGroupCache = [];
      ModalEventBus.close();
    },

    async handleLoadMore() {
      try {
        this.loadingAdditionalData = true;

        // get all application ids and userGroups bound to user
        const [{ results: boundApplications }] = await Promise
          .all([this.fetchBoundApplications(), this.fetchAllUserGroupsBoundToUser()]);

        // get names and userGroup information for each individual application bound to user
        const results = await Promise
          .all(boundApplications.map(this.fetchIndividualApplicationData));

        this.tableData = this.tableData.concat(results);
      } catch {
        notify('error', errorMessage);
      } finally {
        this.loadingAdditionalData = false;
      }
    },
  },
};
</script>

<style module>
@import "../../components/Modal/css/ModalStyles.css";

@value (jcGrey100, jcGrey200) from '~css/brand-colors.css';

.modal {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.modal > footer {
  background-color: jcGrey100;
}

.table {
  font-weight: 700;
  margin: 10px 30px;
}

.table td:first-child {
  text-align: center;
  width: 55px;
}

.table td:nth-child(2) {
  width: 215px;
}

.table td:nth-child(3) {
  width: 160px;
}
</style>
